import { isEmpty } from 'lodash';
import React, { useMemo } from 'react';
import { LuBanknote, LuFileText, LuMapPin, LuReceipt } from 'react-icons/lu';

import { BaseCard } from '@/components/BaseCard';
import { formatDate, formatPercent, titleCase } from 'utils';
import { useAccountGroupProfileWithAumIncluded } from 'views/portfolio/account-aum-queries';
import { useEstimatedEpwTaxRates } from 'views/portfolio/tax-profile-queries';

import { getIncomeScheduleText } from '../tax-profile/tax-profile-utils';
import { useTaxProfile } from '../tax-profile/useTaxProfile';

interface TaxProfileCardProps {
    accountGroupGUID: string;
}

export const TaxProfileCard = ({ accountGroupGUID }: TaxProfileCardProps) => {
    const { data: profile } = useAccountGroupProfileWithAumIncluded(accountGroupGUID);
    const { groupedTaxableIncome, groupedTaxFilings, groupedTaxResidencies, isLoading } =
        useTaxProfile(accountGroupGUID);

    const accountGroup = profile?.accountGroup;
    const withdrawalTaxRate = accountGroup?.withdrawalTaxRate;
    const hasOverride = withdrawalTaxRate !== undefined && withdrawalTaxRate !== null;

    const shouldFetchCalculatedRate = !isLoading && !hasOverride;

    const currentYear = new Date().getFullYear();
    const startDate = shouldFetchCalculatedRate ? formatDate(new Date(currentYear, 0, 1), 'SERVER') : undefined;
    const endDate = shouldFetchCalculatedRate ? formatDate(new Date(currentYear, 11, 31), 'SERVER') : undefined;

    const { data: taxRatesData } = useEstimatedEpwTaxRates(accountGroupGUID, startDate, endDate);

    const calculatedTaxRate = useMemo(() => {
        if (!taxRatesData?.taxDetails) return null;
        const taxDetailsArray = Object.entries(taxRatesData.taxDetails);
        if (taxDetailsArray.length === 0) return null;
        const firstYearDetails = taxDetailsArray[0][1];
        return (Number(firstYearDetails.taxRate) * 100).toFixed(2);
    }, [taxRatesData]);

    const activeIncomes = groupedTaxableIncome.active;
    const activeFilings = groupedTaxFilings.active;
    const activeResidencies = groupedTaxResidencies.active;

    const hasActiveItems = !isEmpty(activeIncomes) || !isEmpty(activeFilings) || !isEmpty(activeResidencies);

    return (
        <BaseCard
            title="Tax Profile"
            icon={LuReceipt}
            loading={isLoading}
            headerClassName="bg-[#C6E6E6]"
            navigateUrl="tax-profile"
        >
            <div className="text-sm font-medium text-gray-700">Withdrawal Tax Rate</div>
            <div className="mt-1 mb-4 text-sm text-gray-600">
                {hasOverride
                    ? `${formatPercent(parseFloat(withdrawalTaxRate) * 100)} (manual override)`
                    : calculatedTaxRate
                      ? `${calculatedTaxRate}% (calculated for ${new Date().getFullYear()})`
                      : 'Set by tax profile'}
            </div>

            {!hasOverride && !hasActiveItems && (
                <div className="text-sm text-gray-500">No active tax profile entries</div>
            )}

            {!isEmpty(activeIncomes) && (
                <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <LuBanknote className="h-4 w-4" />
                        Active Taxable Incomes
                    </div>
                    {activeIncomes.map((income) => (
                        <div
                            key={income.taxableIncomeGUID}
                            className="border-border relative rounded-lg border bg-gray-50 p-3"
                        >
                            <div className="text-sm font-medium text-gray-800">{income.title.plainText}</div>
                            <div className="mt-1 text-xs text-gray-600">{titleCase(income.taxableIncomeType)}</div>
                            <div className="mt-1 text-xs text-gray-700">
                                ${income.amount} {getIncomeScheduleText(income)}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!isEmpty(activeFilings) && (
                <div className="mt-6 space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <LuFileText className="h-4 w-4" />
                        Active Tax Filing
                    </div>
                    {activeFilings.map((filing) => (
                        <div
                            key={filing.taxFilingGUID}
                            className="border-border relative rounded-lg border bg-gray-50 p-3"
                        >
                            <div className="text-sm font-medium text-gray-800">{filing.title.plainText}</div>
                            <div className="mt-1 text-xs text-gray-600">{titleCase(filing.taxFilingStatus)}</div>
                            <div className="mt-1 text-xs text-gray-700">
                                Effective: {formatDate(filing.effectiveFrom)}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!isEmpty(activeResidencies) && (
                <div className="mt-6 space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <LuMapPin className="h-4 w-4" />
                        Active Tax Residency
                    </div>
                    {activeResidencies.map((residency) => (
                        <div
                            key={residency.taxResidencyGUID}
                            className="border-border relative rounded-lg border bg-gray-50 p-3"
                        >
                            <div className="text-sm font-medium text-gray-800">{residency.title.plainText}</div>
                            <div className="mt-1 text-xs text-gray-600">Postal Code: {residency.postalCode}</div>
                            <div className="mt-1 text-xs text-gray-700">
                                Effective: {formatDate(residency.effectiveFrom)}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </BaseCard>
    );
};
